import { format } from "date-fns";
import intl from "react-intl-universal";
import {
  downloadCsv,
  endOfDay,
  formatFilterAmount,
  formatFilterDate,
  fromDateInputValue,
  labelById,
  parseAmountInput,
  startOfDay,
  toDateInputValue,
  toggleFilterListValue,
} from "../../../Components/Molecules/GridHistory/gridFilterUtils";
import { exportStablesHistoryFromApi } from "../../../Hooks/useStablesHistory";
import {
  formatBrl,
  formatPurchaseHistoryTitle,
  formatSaleHistoryTitle,
  formatStable,
  formatSwapHistoryTitle,
  formatWithdrawalHistoryTitle,
  getAsset,
  getAssetLabel,
  getStablesStatusLabel,
  parseStablesDate,
} from "./constants";

export {
  fromDateInputValue,
  toDateInputValue,
  toggleFilterListValue,
};

export const EMPTY_STABLES_HISTORY_FILTERS = {
  dateFrom: null,
  dateTo: null,
  amountMin: "",
  amountMax: "",
  currencies: [],
  statuses: [],
  paymentMethods: [],
  searchQuery: "",
};

const STABLES_HISTORY_CURRENCY_IDS = ["usdc", "usdt", "brl1"];

export const STABLES_HISTORY_CURRENCIES = STABLES_HISTORY_CURRENCY_IDS.map((id) => ({
  id,
  label: getAssetLabel(id),
}));

export const getStablesHistoryStatuses = () => [
  { id: "concluida", label: intl.get("stables.statusCompleted") },
  { id: "processando", label: intl.get("stables.statusProcessing") },
];

export const getStablesHistoryPaymentMethods = () => [
  { id: "saldo_conta", label: intl.get("stables.paymentAccountBalance") },
  { id: "credito_conta", label: intl.get("stables.paymentAccountCredit") },
  { id: "conversao_interna", label: intl.get("stables.paymentInternalConversion") },
  { id: "carteira_externa", label: intl.get("stables.paymentExternalWallet") },
  { id: "conta_bancaria", label: intl.get("stables.paymentBankAccount") },
];

/** @deprecated Use getStablesHistoryStatuses() at render time for i18n */
export const STABLES_HISTORY_STATUSES = [
  { id: "concluida", label: "Concluída" },
  { id: "processando", label: "Em processamento" },
];

/** @deprecated Use getStablesHistoryPaymentMethods() at render time for i18n */
export const STABLES_HISTORY_PAYMENT_METHODS = [
  { id: "saldo_conta", label: "Saldo em conta" },
  { id: "credito_conta", label: "Crédito em conta" },
  { id: "conversao_interna", label: "Conversão interna" },
  { id: "carteira_externa", label: "Carteira externa" },
  { id: "conta_bancaria", label: "Conta bancária" },
];

const normalizeStatus = (status) => {
  const value = String(status || "").toLowerCase();
  if (value.includes("process")) return "processando";
  return "concluida";
};

export const getHistoryItemPaymentMethodId = (item) => {
  switch (item.historyKind) {
    case "purchases":
      return "saldo_conta";
    case "sales":
      return "credito_conta";
    case "swaps":
      return "conversao_interna";
    case "withdrawals":
      return item.destination?.toLowerCase().includes("banc")
        ? "conta_bancaria"
        : "carteira_externa";
    default:
      return null;
  }
};

export const getHistoryItemCurrencies = (item) => {
  const currencies = new Set();

  switch (item.historyKind) {
    case "purchases":
      currencies.add(item.asset);
      break;
    case "sales":
      currencies.add(item.from);
      break;
    case "swaps":
      currencies.add(item.from);
      currencies.add(item.to);
      break;
    case "withdrawals":
      currencies.add(item.asset);
      break;
    default:
      break;
  }

  return [...currencies];
};

export const getHistoryItemAmountBrl = (item) => {
  switch (item.historyKind) {
    case "purchases":
      return Number(item.amountBrl) || 0;
    case "sales":
      return Number(item.amountTo) || 0;
    case "swaps": {
      const asset = getAsset(item.to);
      return asset ? (Number(item.amountTo) || 0) * asset.rate : 0;
    }
    case "withdrawals": {
      const asset = getAsset(item.asset);
      return asset ? (Number(item.amount) || 0) * asset.rate : 0;
    }
    default:
      return 0;
  }
};

export const hasActiveStablesHistoryFilters = (filters) =>
  Boolean(filters.dateFrom)
  || Boolean(filters.dateTo)
  || filters.amountMin !== ""
  || filters.amountMax !== ""
  || filters.currencies.length > 0
  || filters.statuses.length > 0
  || filters.paymentMethods.length > 0
  || Boolean(filters.searchQuery?.trim());

const normalizeSearchToken = (value) =>
  String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w]/g, "");

const buildStablesSearchHaystack = (item) => {
  const amountBrl = getHistoryItemAmountBrl(item);
  const titleByKind = {
    purchases: formatPurchaseHistoryTitle(item),
    sales: formatSaleHistoryTitle(item),
    swaps: formatSwapHistoryTitle(item),
    withdrawals: formatWithdrawalHistoryTitle(item),
  };

  return [
    item.id,
    item.date,
    item.status,
    item.historyKind,
    item.asset,
    item.from,
    item.to,
    item.destination,
    item.amountStable,
    item.amountFrom,
    item.amountTo,
    item.amount,
    item.amountBrl,
    item.rate,
    amountBrl,
    formatBrl(amountBrl),
    formatBrl(item.amountBrl),
    formatBrl(item.amountTo),
    item.asset ? formatStable(item.amountStable ?? item.amount, item.asset) : "",
    item.from ? formatStable(item.amountFrom, item.from) : "",
    item.to ? formatStable(item.amountTo, item.to) : "",
    item.asset ? getAssetLabel(item.asset) : "",
    item.from ? getAssetLabel(item.from) : "",
    item.to ? getAssetLabel(item.to) : "",
    titleByKind[item.historyKind],
  ]
    .filter((value) => value !== "" && value != null)
    .join(" ");
};

const matchesStablesSearchQuery = (haystack, query) => {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;

  const rawHaystack = haystack.toLowerCase();
  const normalizedHaystack = normalizeSearchToken(haystack);

  return tokens.every((token) => {
    if (rawHaystack.includes(token)) return true;
    const normalizedToken = normalizeSearchToken(token);
    return normalizedToken.length > 0 && normalizedHaystack.includes(normalizedToken);
  });
};

export const applyStablesClientFilters = (items, filters) => {
  if (!filters.searchQuery?.trim()) return items;

  const query = filters.searchQuery.trim();
  return items.filter((item) =>
    matchesStablesSearchQuery(buildStablesSearchHaystack(item), query),
  );
};

export const applyStablesHistoryFilters = (items, filters) => {
  const minAmount = parseAmountInput(filters.amountMin);
  const maxAmount = parseAmountInput(filters.amountMax);
  const dateFrom = filters.dateFrom ? startOfDay(filters.dateFrom) : null;
  const dateTo = filters.dateTo ? endOfDay(filters.dateTo) : null;

  return items.filter((item) => {
    const itemDate = parseStablesDate(item.date);

    if (dateFrom && itemDate < dateFrom) return false;
    if (dateTo && itemDate > dateTo) return false;

    const amountBrl = getHistoryItemAmountBrl(item);
    if (minAmount !== null && amountBrl < minAmount) return false;
    if (maxAmount !== null && amountBrl > maxAmount) return false;

    if (filters.currencies.length > 0) {
      const itemCurrencies = getHistoryItemCurrencies(item);
      const matchesCurrency = filters.currencies.some((currency) =>
        itemCurrencies.includes(currency),
      );
      if (!matchesCurrency) return false;
    }

    if (filters.statuses.length > 0) {
      const itemStatus = normalizeStatus(item.status);
      if (!filters.statuses.includes(itemStatus)) return false;
    }

    if (filters.paymentMethods.length > 0) {
      const paymentMethodId = getHistoryItemPaymentMethodId(item);
      if (!paymentMethodId || !filters.paymentMethods.includes(paymentMethodId)) return false;
    }

    if (filters.searchQuery?.trim()) {
      const haystack = buildStablesSearchHaystack(item);
      if (!matchesStablesSearchQuery(haystack, filters.searchQuery.trim())) return false;
    }

    return true;
  });
};

export const buildStablesHistoryFilterTags = (filters) => {
  const statuses = getStablesHistoryStatuses();
  const paymentMethods = getStablesHistoryPaymentMethods();
  const tags = [];

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom) {
    tags.push({
      key: "dateFrom",
      iconType: "date",
      value: formatFilterDate(filters.dateFrom),
    });
  }

  if (filters.dateTo) {
    tags.push({
      key: "dateTo",
      iconType: "date",
      value: formatFilterDate(filters.dateTo),
    });
  }

  if (filters.amountMin !== "") {
    tags.push({
      key: "amountMin",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMin),
    });
  }

  if (filters.amountMax !== "") {
    tags.push({
      key: "amountMax",
      iconType: "amount",
      value: formatFilterAmount(filters.amountMax),
    });
  }

  if (filters.currencies.length > 0) {
    tags.push({
      key: "currencies",
      iconType: "currency",
      value: filters.currencies
        .map((currency) => labelById(STABLES_HISTORY_CURRENCIES, currency))
        .join(", "),
    });
  }

  if (filters.statuses.length > 0) {
    tags.push({
      key: "statuses",
      iconType: "status",
      value: filters.statuses
        .map((status) => labelById(statuses, status))
        .join(", "),
    });
  }

  if (filters.paymentMethods.length > 0) {
    tags.push({
      key: "paymentMethods",
      iconType: "payment",
      value: filters.paymentMethods
        .map((method) => labelById(paymentMethods, method))
        .join(", "),
    });
  }

  return tags;
};

export const removeStablesHistoryFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "dateFrom":
      return { ...filters, dateFrom: null };
    case "dateTo":
      return { ...filters, dateTo: null };
    case "amountMin":
      return { ...filters, amountMin: "" };
    case "amountMax":
      return { ...filters, amountMax: "" };
    case "currencies":
      return { ...filters, currencies: [] };
    case "statuses":
      return { ...filters, statuses: [] };
    case "paymentMethods":
      return { ...filters, paymentMethods: [] };
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    default:
      return filters;
  }
};

const getHistoryKindLabel = (historyKind) => {
  switch (historyKind) {
    case "purchases":
      return intl.get("stables.typePurchase");
    case "sales":
      return intl.get("stables.typeSale");
    case "swaps":
      return intl.get("stables.typeSwap");
    case "withdrawals":
      return intl.get("stables.typeWithdrawal");
    default:
      return historyKind;
  }
};

export const exportStablesHistoryCsv = async (items, { type = "all", filters = EMPTY_STABLES_HISTORY_FILTERS } = {}) => {
  const apiExport = await exportStablesHistoryFromApi({ type, filters, page: 1, pageSize: 10000 });
  if (apiExport?.csvContent) {
    const blob = new Blob([apiExport.csvContent], { type: apiExport.contentType || "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = apiExport.filename || `historico-stables-${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    return;
  }

  const paymentMethods = getStablesHistoryPaymentMethods();
  const headers = [
    intl.get("grid.date"),
    intl.get("grid.transaction"),
    intl.get("grid.value"),
    intl.get("grid.status"),
    intl.get("stables.filterPaymentMethod"),
  ];

  const rows = items.map((item) => {
    const paymentMethod = labelById(
      paymentMethods,
      getHistoryItemPaymentMethodId(item),
    );

    return [
      item.date,
      getHistoryKindLabel(item.historyKind),
      getHistoryItemAmountBrl(item).toFixed(2).replace(".", ","),
      getStablesStatusLabel(item.status),
      paymentMethod,
    ];
  });

  downloadCsv(headers, rows, `historico-stables-${format(new Date(), "yyyy-MM-dd")}.csv`);
};

export const isChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "amount":
      return filters.amountMin !== "" || filters.amountMax !== "";
    case "currency":
      return filters.currencies.length > 0;
    case "status":
      return filters.statuses.length > 0;
    case "payment":
      return filters.paymentMethods.length > 0;
    case "more":
      return filters.currencies.length > 0
        || filters.statuses.length > 0
        || filters.paymentMethods.length > 0;
    default:
      return false;
  }
};
